import {
  mapPaginatedResponse,
  setArrayFilter,
  toQueryParams,
  unwrapApiData,
  type ApiClient,
} from '@klareospace/pharos-lib-front-utils';
import {
  ORDER_HISTORY_EVENT_CODE,
  type Contact,
  type CreateDeliveryAddress,
  type CreateOrder,
  type HistoryEntry,
  type Measurement,
  type Order,
  type OrderHistoryEventCode,
  type OrderStatus,
  type PaginatedResponse,
  type PaginationParams,
  type PharosApiResponse,
  type PharosPaginatedResponse,
  type SortParams,
  type Task,
  type TimeSlot,
  type VolumeUnit,
  type WeightUnit,
} from '@klareospace/pharos-lib-types';

import type { OrderSortableColumn } from '../types/order.types';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Single-page fetch size for the history feed — no infinite scroll in this cycle (PHA-624). */
const ORDER_HISTORY_FETCH_LIMIT = 100;

/** Single-page pagination params shared by the unfiltered and status-only history fetches. */
const ORDER_HISTORY_PAGE_PARAMS = { page: 1, pageSize: ORDER_HISTORY_FETCH_LIMIT };

/**
 * The `created` entry is unique per order (one sentinel per history, since the PHA-690 curation
 * it is also the sole carrier of the order's first stable status) and always the oldest entry —
 * a page size of 1 is enough and guarantees it survives even when ≥100 status entries would
 * otherwise push it off the status page.
 */
const ORDER_CREATED_ENTRY_PAGE_PARAMS = { page: 1, pageSize: 1 };

// ─── Types ────────────────────────────────────────────────────────────────────

export type OrderSearchResult = PaginatedResponse<Order>;
export type OrderHistoryResult = PaginatedResponse<HistoryEntry>;

export interface SearchOrdersParams extends PaginationParams {
  carrierIds?: string[];
  dateFrom?: string;
  dateTo?: string;
  driverIds?: string[];
  orderTypeIds?: string[];
  /** Exact-match reference lookup (duplicate check) — distinct from the fuzzy `search` filter. */
  referenceNumber?: string;
  search?: string;
  shipperIds?: string[];
  siteId?: string;
  sort?: SortParams<OrderSortableColumn>;
  spaceId?: string;
  statuses?: OrderStatus[];
  unassignedDriver?: boolean;
}

/**
 * Operator-facing PATCH /orders/:id body. Mirrors the backend's UpdateOrderByIdBody
 * (packages/pharos-lib-back-utils/src/mongo/dto/order.dto.ts) — only the editable
 * surface described by pharos-lib-types' order-update-fields categorization.
 *
 * `totalHandlingUnitQuantity`/`totalVolume`/`totalWeight` are declared explicitly rather
 * than derived from `Order`: the PATCH body accepts the raw value, while `Order` stores
 * them wrapped in `Computable<T>`.
 */
export type UpdateOrderRequest = Partial<
  Pick<Order, 'handlingUnits' | 'instruction' | 'orderValue' | 'requestedTimeWindow' | 'tags'>
> & {
  customer?: { email?: string; phone?: Contact['phone'] };
  delivery?: { address?: CreateDeliveryAddress; contact?: Contact | null; durationTime?: number; timeSlot?: TimeSlot };
  pickup?: { contact?: Contact | null; durationTime?: number; timeSlot?: TimeSlot };
  requestedCarrierCode?: string;
  totalHandlingUnitQuantity?: number;
  totalVolume?: Measurement<VolumeUnit>;
  totalWeight?: Measurement<WeightUnit>;
};

// ─── API Factory ──────────────────────────────────────────────────────────────

export function createOrdersApi(client: ApiClient): {
  cancelOrder: (id: string) => Promise<Order>;
  createOrder: (body: CreateOrder) => Promise<Order>;
  getOrder: (id: string) => Promise<Order>;
  getOrderHistory: (id: string) => Promise<OrderHistoryResult>;
  getOrderStatusHistory: (id: string) => Promise<OrderHistoryResult>;
  getOrderTasks: (orderId: string) => Promise<Task[]>;
  searchOrders: (params: SearchOrdersParams) => Promise<OrderSearchResult>;
  updateOrder: (id: string, body: UpdateOrderRequest) => Promise<Order>;
} {
  return {
    cancelOrder: async (id: string): Promise<Order> => {
      const response = await client.execute<PharosApiResponse<Order>>(() =>
        client.instance.post(`/orders/${id}/cancel`),
      );
      return unwrapApiData(response);
    },
    createOrder: async (body: CreateOrder): Promise<Order> => {
      const response = await client.execute<PharosApiResponse<Order>>(() => client.instance.post('/orders', body));
      return unwrapApiData(response);
    },
    getOrder: async (id: string): Promise<Order> => {
      const response = await client.execute<PharosApiResponse<Order>>(() => client.instance.get(`/orders/${id}`));
      return unwrapApiData(response);
    },
    getOrderHistory: async (id: string): Promise<OrderHistoryResult> => {
      const query = toQueryParams(ORDER_HISTORY_PAGE_PARAMS);
      const response = await client.execute<PharosApiResponse<PharosPaginatedResponse<HistoryEntry>>>(() =>
        client.instance.get(`/orders/${id}/history`, { params: query }),
      );
      return mapPaginatedResponse(unwrapApiData(response));
    },
    getOrderStatusHistory: async (id: string): Promise<OrderHistoryResult> => {
      const [statusPage, createdPage] = await Promise.all([
        fetchOrderHistoryPage(client, id, ORDER_HISTORY_PAGE_PARAMS, ORDER_HISTORY_EVENT_CODE.STATUS),
        fetchOrderHistoryPage(client, id, ORDER_CREATED_ENTRY_PAGE_PARAMS, ORDER_HISTORY_EVENT_CODE.CREATED),
      ]);
      return mergeStatusAndCreatedPages(statusPage, createdPage);
    },
    getOrderTasks: async (orderId: string): Promise<Task[]> => {
      const response = await client.execute<PharosApiResponse<Task[]>>(() =>
        client.instance.get(`/orders/${orderId}/tasks`),
      );
      return unwrapApiData(response);
    },
    searchOrders: async (params: SearchOrdersParams): Promise<OrderSearchResult> => {
      const body = toSearchBody(params);
      const query = toQueryParams(params);
      const response = await client.execute<PharosApiResponse<PharosPaginatedResponse<Order>>>(() =>
        client.instance.post('/orders/search', body, { params: query }),
      );
      return mapPaginatedResponse(unwrapApiData(response));
    },
    updateOrder: async (id: string, body: UpdateOrderRequest): Promise<Order> => {
      const response = await client.execute<PharosApiResponse<Order>>(() =>
        client.instance.patch(`/orders/${id}`, body),
      );
      return unwrapApiData(response);
    },
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toSearchBody(params: SearchOrdersParams): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  applyScalarFilters(body, params);
  applyArrayFilters(body, params);
  return body;
}

function applyScalarFilters(body: Record<string, unknown>, params: SearchOrdersParams): void {
  const trimmedSearch = params.search?.trim();
  if (trimmedSearch) body['search'] = trimmedSearch;
  if (params.siteId) body['siteId'] = params.siteId;
  if (params.spaceId) body['spaceId'] = params.spaceId;
  if (params.dateFrom) body['dateFrom'] = params.dateFrom;
  if (params.dateTo) body['dateTo'] = params.dateTo;
  if (params.unassignedDriver) body['unassignedDriver'] = true;
  if (params.referenceNumber) body['referenceNumber'] = params.referenceNumber;
}

function applyArrayFilters(body: Record<string, unknown>, params: SearchOrdersParams): void {
  setArrayFilter(body, 'carrierIds', params.carrierIds);
  setArrayFilter(body, 'driverIds', params.driverIds);
  setArrayFilter(body, 'orderTypeIds', params.orderTypeIds);
  setArrayFilter(body, 'shipperIds', params.shipperIds);
  setArrayFilter(body, 'statuses', params.statuses);
}

/** Fetches one page of `/orders/:id/history` filtered to a single event code. */
async function fetchOrderHistoryPage(
  client: ApiClient,
  id: string,
  pageParams: PaginationParams,
  code: OrderHistoryEventCode,
): Promise<OrderHistoryResult> {
  const query = { ...toQueryParams(pageParams), code };
  const response = await client.execute<PharosApiResponse<PharosPaginatedResponse<HistoryEntry>>>(() =>
    client.instance.get(`/orders/${id}/history`, { params: query }),
  );
  return mapPaginatedResponse(unwrapApiData(response));
}

/**
 * Merges the status page with the `created` sentinel page. Both arrive most-recent-first and
 * `created` is always the oldest entry, so appending it preserves ordering. `total` is the status
 * page's count plus the one extra sentinel entry — consumers only read `items`.
 */
function mergeStatusAndCreatedPages(
  statusPage: OrderHistoryResult,
  createdPage: OrderHistoryResult,
): OrderHistoryResult {
  return {
    ...statusPage,
    items: [...statusPage.items, ...createdPage.items],
    total: statusPage.total + createdPage.items.length,
  };
}
